import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { TableConfig } from '@app/shared/util/table-config.utils';
import { Group } from 'app/tool/model/group.model';
import { TranslateService } from "@ngx-translate/core";
import { TableComponent } from '@components/custom-table/table.component';
import { Log } from "ng2-logger";
import { LineService } from "@services/line.service";
import { ScannerDirective } from '@app/shared/directives/scanner.directive';
import { ToolUsageGroup } from '@app/tool/model/tool-usage-group.model';
import { map } from "rxjs/operators";

@Component({
    selector: 'app-tools-usage-table',
    templateUrl: 'tools-usage-table.component.html',
    styleUrls: ['tools-usage-table.component.scss']
})
export class ToolsUsageTableComponent implements OnInit {
    _logger = Log.create(ToolsUsageTableComponent.name);

    @ViewChild(TableComponent, {static: false}) table: TableComponent;

    @Input() toolsGrouped = new Array<Group>();
    @Input() tableConfig: TableConfig;
    @Input() treeMode = false;
    @Input() isLoading = false;
    @Input() showFilters = true;
    @Input() showGroupFilters = false;
    @Input() showGroupers = true;
    @Input() headerBackgroundColor: string;
    @Input() filterBySecLinesId: number;
    @Input() isFilterExpanded = true;
    @Input() hasTitle = false;
    @Input() notMyProblem = true;

    @Output() pageChange = new EventEmitter<any>();
    @Output() filterChange = new EventEmitter<any>();
    @Output() filterGroupChange = new EventEmitter<any>();
    @Output() filterBySecLines = new EventEmitter<any>();
    @Output() groupChange = new EventEmitter<any>();
    @Output() sortChange = new EventEmitter<any>();
    @Output() openDetais = new EventEmitter<any>();
    @Output() openDetailsNewPage = new EventEmitter<any>();
    @Output() openTicket = new EventEmitter<any>();
    @Output() openRevisionModal = new EventEmitter<any>();

    linesList = [];

    onInit = true;
    groupableColumns = [];

    groupFilters = [];
    cleaningColorList = [];
    maintenanceColorList = [];
    measurementsColorList = [];
    toolStatusList = [];

    // TODO datepicker component does not get translated dynamically.
    // This needs fixing
    constructor(private translateService: TranslateService,
                private lineService: LineService,
                private cdr: ChangeDetectorRef) {
        this.groupableColumns = this.buildGroupableColumnKeys();
        this.groupFilters = this.buildGroupFilters();
        this.translateService.onLangChange.subscribe((event) => {
            this.groupableColumns = this.buildGroupableColumnKeys();
            this.groupFilters = this.buildGroupFilters();
            this.fillColorList();
            this.rebuildAllOptionForGroupFilter();
            this.fillToolStatusList();
            this.table.onExpandAll(true);
        });

    }

    ngOnInit() {
        this.filterGroupChange.emit(this.tableConfig);
        this.getLines();
        this.fillColorList();
        this.fillToolStatusList();
    }

    fillColorList() {
        this.cleaningColorList = [
            {
                id: '',
                name: this.translateService.instant('admin.toolsUsage.color.all')
            },
            {
                id: 75,
                name: this.translateService.instant('admin.toolsUsage.color.green')
            },
            {
                id: 90,
                name: this.translateService.instant('admin.toolsUsage.color.yellow')
            },
            {
                id: 100,
                name: this.translateService.instant('admin.toolsUsage.color.red')
            },
        ];
        this.maintenanceColorList = [...this.cleaningColorList];
        this.measurementsColorList = [...this.cleaningColorList];
    }

    private addAllOptionForSelect(response, itemsName) {
        response[itemsName].unshift({
            id: '',
            name: this.translateService.instant('admin.tools.table.groupFilter.all.' + itemsName)
        });
        response.total++;
        return response;
    }

    fillToolStatusList() {
        this.toolStatusList = [
            {
                id: 0,
                name: this.translateService.instant('generic.statuses.all')
            },
            {
                id: 1,
                name: this.translateService.instant('generic.statuses.storedInLift')
            },
            {
                id: 2,
                name: this.translateService.instant('generic.statuses.atAtomStation')
            },
            {
                id: 3,
                name: this.translateService.instant('generic.statuses.inTransport')
            },
            {
                id: 4,
                name: this.translateService.instant('generic.statuses.atLine')
            },
            {
                id: 5,
                name: this.translateService.instant('generic.statuses.atFacility')
            }
        ];
    }

    private getLines() {
        this.lineService.getAllLines().pipe(
            map(
                res => this.addAllOptionForSelect(res, 'items'))
        ).subscribe(
            response => {
                this.groupFilters = this.buildGroupFilters(response.items, 0);
                this.linesList = response.items;
            },
            error => {
                this._logger.error("getLines::", error);
            },
        )
    }

    buildGroupFilters(items = [], id = -1) {
        return [
            {
                id: 0,
                placeholder: this.translateService.instant("admin.tools.table.grouping.options.line"),
                items: id === 0 && items.length > 0 ? items : ((this.groupFilters.length > 0 && this.groupFilters[0].items) || []),
                displayField: 'name',
                value: "id",
                filterParam: "lineId",
                selectedValue: (this.groupFilters.length > 0 && this.groupFilters[0].selectedValue) ?
                    this.groupFilters[0].selectedValue : '',
            }
        ];
    }

    private rebuildAllOptionForGroupFilter() {
        if (this.groupFilters.length > 0 && this.groupFilters[0].items.length > 0) {
            this.groupFilters[0].items[0].name = this.translateService.instant(
                'admin.tools.table.groupFilter.all.items');
        }
    }

    onToolUidChange(table: any, column: any, scannedValue: string) {
        if (ScannerDirective.UID_REGEX.test(scannedValue)) {
            this.cdr.detectChanges();
            const toolUid = scannedValue.replace(ScannerDirective.UID_REGEX, '$3');
            column.filterValue = toolUid;
            this._logger.info('Tool UID scanned', toolUid);
        }
        table.onFilterChange(column);
    }

    onPageChange(tableConfig: TableConfig) {
        this._logger.info('onPageChange::', tableConfig);
        this.pageChange.emit(tableConfig);
    }

    onSortChange(tableConfig: TableConfig) {
        this._logger.info('onSortChange::', tableConfig);
        this.sortChange.emit(tableConfig);
    }

    onFilterChange(tableConfig: TableConfig) {
        this._logger.info('onFilterChange::', tableConfig);
        this.filterChange.emit(tableConfig);
    }

    onFilterGroupChange(tableConfig: TableConfig) {
        if (this.onInit) {
            this.onInit = false;
            return;
        }
        for (const filter of this.tableConfig.filters) {
            if (filter.field === 'lineId') {
                this.groupFilters[0].selectedValue = filter.value;
            }
        }
        this._logger.info('onFilterGroupChange::', tableConfig);

        if (this.tableConfig.filters[0] === undefined || this.tableConfig.filters[0].value === undefined) {
            this.tableConfig.filters[0] = {value: ""};
        }

        this.filterGroupChange.emit(this.tableConfig);
    }

    onOpenToolDetailsInNewPage(tool) {
        this.openDetailsNewPage.emit(tool);
    }

    isToolSet(record) {
        return record instanceof ToolUsageGroup;
    }

    buildGroupableColumnKeys() {
        return [
            {
                id: 0,
                name: this.translateService.instant("admin.tools.table.grouping.options.line"),
                value: "Line"
            }
        ];
    }

    onOpenTicket(ticketId: number) {
        this.openTicket.emit(ticketId);
    }

    onClickOperation(tool: any, priority: number) {
        if (tool.hasOwnProperty('children') || !this.hasPercentage(tool, priority)) {
            return;
        }
        this.openRevisionModal.emit({toolId: tool.toolId, priority: priority});
    }

    canRenderPointer(record: any, number: number) {
        return !record.hasOwnProperty('children') && this.hasPercentage(record, number);
    }

    private hasPercentage(record: any, priority: number) {
        switch (priority) {
            case 1:
                return record.cleaningPercentage !== 0;
            case 2:
                return record.maintenancePercentage !== 0;
            case 3:
                return record.measurementsPercentage !== 0;
            default:
                return false;
        }
    }
}
